import React from "react";
import AppShell from "@/components/AppShell";
import { CheckCircleIcon as CheckCircle, WarningIcon as Warning, XCircleIcon as XCircle, ArrowSquareOutIcon as ArrowSquareOut, ReceiptIcon as Receipt, MapPinIcon as MapPin, QuestionIcon as Question, ScalesIcon as Scales, BuildingsIcon as Buildings } from "@phosphor-icons/react";

/**
 * Admin / Stripe Tax setup playbook.
 *
 * Lives at /admin/stripe-tax-setup. Comprehensive reference for the
 * founder so when a breeder, storefront seller, or service provider
 * asks "can Stripe collect & hold sales tax for me?", there's a
 * canonical answer they can DM/email back without re-researching.
 *
 * Why a dedicated page (not a README): this question gets asked at
 * least once per persona (breeders, storefronts, providers), and the
 * answer is different per persona. Centralising it in admin removes
 * the founder from being the bottleneck.
 *
 * Currently:
 *   - Storefront product sales      → Stripe Tax ENABLED ✅
 *   - Platform billing (one-offs)   → Stripe Tax ENABLED ✅
 *   - Service bookings (one-off)    → Stripe Tax ENABLED ✅
 *   - Service bookings (recurring)  → Stripe Tax ENABLED ✅
 *   - Breeder dog escrow sales      → Stripe Tax ENABLED ✅ (Iter 203 refactor)
 *
 * All 5 revenue surfaces are now on the same destination-charge +
 * automatic_tax + InvalidRequestError-fallback pattern. Breeder/
 * seller/provider is the Merchant of Record for tax purposes;
 * Armstrong K9 Registry stays a pure venue.
 *
 * Each section includes the file + line numbers where each flow lives
 * so future agents can grep + verify the state hasn't drifted.
 */

const FLOWS = [
  {
    id: "storefronts",
    title: "Storefront product sales",
    persona: "Storefront sellers — food, merch, accessories, gear",
    status: "live",
    file: "routers/storefronts.py:712, 1304",
    summary: "Stripe Tax enabled with automatic fallback to no-tax if the connected account hasn't completed origin-address setup.",
    seller_action: "Seller must (a) register with CA CDTFA → get Seller's Permit and (b) complete Stripe Tax origin-address setup inside their Stripe dashboard. After that, every storefront checkout itemises tax automatically.",
  },
  {
    id: "platform",
    title: "Platform billing (one-off purchases)",
    persona: "Anyone buying registration fees, background-check fees, breeding-inquiry credits, etc.",
    status: "live",
    file: "routers/billing/routes.py:954",
    summary: "Platform-level charges through Armstrong's own Stripe account. Tax calculated where applicable (most digital-fee SKUs are non-taxable in CA).",
    seller_action: "Nothing — Armstrong K9 Registry is merchant of record on these. We file ourselves once we cross the CA tangible-personal-property nexus threshold.",
  },
  {
    id: "service-bookings-single",
    title: "Service bookings (single session)",
    persona: "Trainers, groomers, walkers, sitters — one-off bookings",
    status: "live",
    file: "routers/bookings.py:435",
    summary: "Stripe Tax enabled this iteration. California doesn't tax most professional services, so Stripe Tax typically calculates $0 — but the tax dashboard captures gross revenue per provider for filing accuracy.",
    seller_action: "Provider should register with CA CDTFA + configure Stripe Tax origin address if they're selling any tangible add-ons (grooming products, training treats, etc.) along with the service. Pure service bookings stay $0 tax automatically.",
  },
  {
    id: "service-bookings-recurring",
    title: "Service bookings (recurring / series)",
    persona: "Trainers, walkers, sitters — weekly / bi-weekly recurring series",
    status: "live",
    file: "routers/bookings.py:678",
    summary: "Same fallback pattern. First session captured + Stripe Customer created with off_session setup_future_usage; subsequent sessions reuse the saved payment method.",
    seller_action: "Same as one-off bookings.",
  },
  {
    id: "escrow",
    title: "Breeder dog sales (escrow)",
    persona: "Breeders selling puppies / grown dogs via the marketplace",
    status: "live",
    file: "routers/escrow.py:226",
    summary: "Stripe Tax ENABLED as of the Iter 203 escrow refactor (destination charges + manual capture). Tax flows directly to the breeder's Stripe Tax dashboard with the same InvalidRequestError → strip-tax fallback pattern as storefronts + bookings.",
    seller_action: "Breeder must (a) register with CA CDTFA → Seller's Permit (live dog sales = tangible personal property), (b) complete Stripe Tax origin-address setup in their Stripe Connect dashboard. After that, every escrow checkout itemises tax automatically and the tax portion is held on the breeder's account for filing.",
  },
];

const FAQ = [
  {
    q: "Do live dog sales actually need sales tax in California?",
    a: (
      <>
        Yes — California treats live pet sales as tangible personal property (CDTFA Reg 1587 carves out livestock-for-slaughter; pets are NOT exempt). However, many hobby breeders fall under "occasional sale" thresholds and don&apos;t currently collect. Once we cross <strong>$500K in CA marketplace sales</strong>, CA&apos;s AB 147 marketplace-facilitator law triggers and <strong>Armstrong K9 Registry becomes legally required to collect + remit on breeders&apos; behalf</strong> — at that point we&apos;ll need a tax attorney to flip the architecture. Until then, individual breeders are responsible.
      </>
    ),
  },
  {
    q: "What does Stripe Tax actually DO once enabled?",
    a: "Auto-detects buyer location (shipping address → IP fallback), calculates the correct combined state + county + city rate, adds a tax line to the Checkout Session, holds the tax portion as part of the charge, and surfaces filing-period totals in the Stripe Tax dashboard. Stripe does NOT auto-remit to state tax authorities (that's a paid add-on called Stripe Tax Filing — ~$0.50/transaction, opt-in per account).",
  },
  {
    q: "Does enabling Stripe Tax cost the platform anything?",
    a: "Stripe Tax calculation is 0.5% per transaction (capped at $0.50/txn) on top of normal Stripe fees. We pay this on platform-billing charges. For Connect destination charges (storefronts, bookings, future-escrow), the fee comes out of the connected account's payout, so the seller pays it — not us. Stripe Tax Filing (auto-remit) is a separate $1.20/filing add-on, opt-in per connected account.",
  },
  {
    q: "What if a connected account hasn't configured Stripe Tax origin address?",
    a: "Every checkout flow in this codebase wraps Stripe Tax in a try/except that strips automatic_tax + retries on InvalidRequestError. So a seller without Tax configured still gets a working checkout — they just don't get itemised tax until they finish setup. Failure is recoverable, never fatal.",
  },
  {
    q: "Who is the merchant of record (MoR)?",
    a: "Armstrong K9 Registry is the venue, NOT the merchant of record. For storefronts, bookings, and escrow, the SELLER is MoR — they collect tax via Stripe Tax, file with CA themselves. Armstrong K9 Registry is MoR only on platform billing (registration fees, etc.) which we file ourselves. This is the legally correct posture for a multi-sided marketplace below the marketplace-facilitator threshold.",
  },
];

const ESCROW_MIGRATION_STEPS = [
  {
    step: 1,
    title: "Replace emergentintegrations StripeCheckout wrapper with direct stripe SDK call",
    code: "In routers/escrow.py:208, swap `checkout.create_checkout_session(CheckoutSessionRequest(...))` with a direct `stripe.checkout.Session.create(**kwargs)` call following the same pattern as routers/bookings.py:435. Preserve the existing webhook URL + metadata exactly.",
  },
  {
    step: 2,
    title: "Add automatic_tax + tax_behavior with the standard fallback",
    code: "Wrap the create call in the same try/except (stripe.error.InvalidRequestError) pattern: try with automatic_tax, fall back to without if seller hasn't configured Tax origin yet.",
  },
  {
    step: 3,
    title: "Wire Stripe Connect destination charge",
    code: "The escrow flow currently doesn't use Connect destination charges (it does manual stripe.Transfer on release). After Tax migration, switch to destination charges so tax flows naturally with the principal to the seller's Connect account. Application fee remains _live_escrow_fee_bps() basis points.",
  },
  {
    step: 4,
    title: "Test on the Stripe sandbox with a CA buyer",
    code: "Use a test card with billing address in CA. Verify the Checkout Session shows a tax line. Then verify the connected account's Stripe Tax dashboard captures the gross revenue for the filing period.",
  },
  {
    step: 5,
    title: "Add regression test",
    code: "Mirror tests/test_iter112_payment_recovery_and_home_tracker.py:45 — assert escrow source contains automatic_tax + the fallback strip pattern, so future agents don't accidentally drop the tax line.",
  },
];

function FlowRow({ flow }) {
  const StatusIcon =
    flow.status === "live" ? CheckCircle : flow.status === "pending" ? Warning : XCircle;
  const statusColor =
    flow.status === "live"
      ? "text-emerald-600"
      : flow.status === "pending"
      ? "text-amber-600"
      : "text-crimson";
  const statusLabel =
    flow.status === "live" ? "LIVE" : flow.status === "pending" ? "PENDING" : "OFF";
  return (
    <div
      className="border border-[#E5E5E5] rounded-xl bg-white p-5 flex flex-col gap-3"
      data-testid={`stripe-tax-flow-${flow.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.22em] text-gold-dark font-bold">
            {flow.persona}
          </p>
          <h3 className="font-spectral text-xl tracking-tight text-ink mt-1">
            {flow.title}
          </h3>
        </div>
        <span
          className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold ${statusColor}`}
        >
          <StatusIcon size={14} weight="fill" />
          {statusLabel}
        </span>
      </div>
      <p className="text-[13.5px] text-[#3F3F3F] leading-relaxed">{flow.summary}</p>
      <div className="text-[12px] text-[#6B6B6B] grid sm:grid-cols-[80px,1fr] gap-x-3 gap-y-1">
        <span className="font-bold uppercase tracking-wide">File</span>
        <code className="font-mono text-ink">{flow.file}</code>
        <span className="font-bold uppercase tracking-wide">Seller action</span>
        <span>{flow.seller_action}</span>
      </div>
    </div>
  );
}

export default function AdminStripeTaxSetup() {
  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-6 md:px-10 py-10" data-testid="admin-stripe-tax-page">
        {/* Header */}
        <div className="mb-12">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-crimson mb-2">
            Admin · Operations
          </p>
          <h1 className="font-spectral text-4xl md:text-5xl text-ink leading-tight">
            Stripe Tax — what&apos;s collected on behalf of whom
          </h1>
          <p className="mt-3 text-[15px] text-[#3F3F3F] max-w-2xl">
            One canonical reference for the question that gets asked
            once per persona: <em>&quot;Can Stripe collect and hold sales
            tax for me on what I sell through Armstrong?&quot;</em>
            Short answer: yes for storefronts and service bookings,
            with a P1 migration pending for breeder escrow.
          </p>
        </div>

        {/* TL;DR card */}
        <section className="mb-12" data-testid="stripe-tax-tldr">
          <div className="rounded-xl border border-[#E2DAC2] bg-cream/40 p-6">
            <div className="flex items-center gap-2 mb-3">
              <Receipt size={18} weight="duotone" className="text-gold-dark" />
              <p className="font-bold text-ink text-[14.5px]">TL;DR for the breeder + seller asking</p>
            </div>
            <div className="space-y-3 text-[14px] text-[#3F3F3F] leading-relaxed">
              <p>
                <strong className="text-emerald-700">Storefront sellers (dog food / merch / accessories):</strong> Already enabled. Tax is calculated, itemised, and held within the Stripe payment automatically. The seller just needs to (1) get a CA Seller&apos;s Permit from CDTFA and (2) finish the Stripe Tax origin-address step inside their Stripe dashboard. Quarterly filing totals show up in their Stripe Tax dashboard.
              </p>
              <p>
                <strong className="text-emerald-700">Service providers (trainers, groomers, walkers, sitters):</strong> Enabled this iteration. California doesn&apos;t tax pure services, so most bookings will show $0 tax — but Stripe Tax captures the gross revenue anyway for clean filing.
              </p>
              <p>
                <strong className="text-amber-700">Breeders selling dogs (escrow):</strong> Not yet — needs a small SDK migration first (see the playbook below). Today, the breeder receives the gross sale price; they remit CA tax themselves if registered.
              </p>
              <p>
                <strong className="text-ink">Threshold to watch:</strong> Once Armstrong K9 Registry crosses ~$500K in CA-buyer sales annually, California&apos;s marketplace-facilitator law (AB 147) requires the platform to collect + remit on behalf of sellers. We&apos;ll need a tax attorney to flip the architecture before we hit that bar.
              </p>
            </div>
          </div>
        </section>

        {/* Flow-by-flow status */}
        <section className="mb-12" data-testid="stripe-tax-flows">
          <div className="flex items-center gap-2 mb-5">
            <Buildings size={20} weight="duotone" className="text-gold" />
            <h2 className="font-spectral text-3xl text-ink">Flow-by-flow status</h2>
          </div>
          <div className="grid gap-4">
            {FLOWS.map((f) => (
              <FlowRow key={f.id} flow={f} />
            ))}
          </div>
        </section>

        {/* Seller setup checklist */}
        <section className="mb-12" data-testid="stripe-tax-seller-checklist">
          <div className="flex items-center gap-2 mb-5">
            <MapPin size={20} weight="duotone" className="text-gold" />
            <h2 className="font-spectral text-3xl text-ink">What each seller needs to do (once)</h2>
          </div>
          <ol className="space-y-4 text-[14px] text-[#3F3F3F] leading-relaxed list-decimal pl-6">
            <li>
              <strong className="text-ink">Register with CA CDTFA</strong> — apply online at{" "}
              <a
                href="https://onlineservices.cdtfa.ca.gov/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gold-dark underline underline-offset-2 inline-flex items-center gap-1"
              >
                onlineservices.cdtfa.ca.gov <ArrowSquareOut size={12} />
              </a>{" "}
              for a Seller&apos;s Permit. Free, takes ~24 hours. They&apos;ll get a permit number that goes into their Stripe Tax registration list.
            </li>
            <li>
              <strong className="text-ink">Open Stripe Tax inside their Stripe dashboard</strong> —
              from their connected Stripe account: Settings → Tax → Get Started. Enter the business
              origin address (where they ship from / operate). This is required for Stripe to
              calculate destination-based tax correctly.
            </li>
            <li>
              <strong className="text-ink">Register tax obligations</strong> — under Stripe Tax →
              Registrations → Add registration → California. Paste the CDTFA permit number from step
              1. Stripe now knows we&apos;re registered to collect tax in CA.
            </li>
            <li>
              <strong className="text-ink">Pick filing cadence</strong> — Stripe Tax dashboard
              surfaces quarterly + monthly totals. Sellers either file via CA CDTFA themselves
              (free) or opt into Stripe Tax Filing (paid add-on, ~$1.20/filing per state).
            </li>
            <li>
              <strong className="text-ink">Test a real checkout</strong> — buy something on their
              storefront from a CA billing address. Tax line should appear on the receipt. If it
              doesn&apos;t, the connected account&apos;s Tax setup isn&apos;t complete; the booking
              still goes through (we strip + retry) but no tax is itemised.
            </li>
          </ol>
        </section>

        {/* Escrow migration playbook — DONE in Iter 203, kept here as
            historical record so future agents understand WHY the
            current escrow code looks the way it does. */}
        <section className="mb-12" data-testid="stripe-tax-escrow-migration">
          <div className="flex items-center gap-2 mb-5">
            <Scales size={20} weight="duotone" className="text-gold" />
            <h2 className="font-spectral text-3xl text-ink">Escrow migration playbook <span className="text-[14px] text-emerald-600 font-bold align-middle ml-2">✓ DONE (Iter 203)</span></h2>
          </div>
          <p className="text-[14px] text-[#3F3F3F] leading-relaxed mb-6">
            Breeder dog sales used to go through{" "}
            <code className="bg-cream px-1.5 py-0.5 rounded text-[12.5px]">routers/escrow.py</code>{" "}
            via the <code className="bg-cream px-1.5 py-0.5 rounded text-[12.5px]">emergentintegrations</code>{" "}
            wrapper, which didn&apos;t expose <code>automatic_tax</code>. The Iter 203 refactor
            migrated escrow to direct Stripe SDK calls with destination charges + manual capture +
            Stripe Tax fallback. Kept below as the historical migration record.
          </p>
          <ol className="space-y-5">
            {ESCROW_MIGRATION_STEPS.map((s) => (
              <li
                key={s.step}
                className="border border-[#E5E5E5] rounded-xl bg-white p-5 opacity-80"
                data-testid={`escrow-migration-step-${s.step}`}
              >
                <div className="flex items-start gap-4">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 font-bold text-[14px] flex-shrink-0">
                    ✓
                  </span>
                  <div className="min-w-0">
                    <p className="font-bold text-ink text-[14.5px]">{s.title}</p>
                    <p className="text-[13.5px] text-[#3F3F3F] leading-relaxed mt-2">{s.code}</p>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* FAQ */}
        <section className="mb-12" data-testid="stripe-tax-faq">
          <div className="flex items-center gap-2 mb-5">
            <Question size={20} weight="duotone" className="text-gold" />
            <h2 className="font-spectral text-3xl text-ink">FAQ</h2>
          </div>
          <div className="space-y-5 text-[14px] text-[#3F3F3F]">
            {FAQ.map((item, i) => (
              <div key={i} data-testid={`stripe-tax-faq-${i}`}>
                <p className="font-bold text-ink mb-1.5">{item.q}</p>
                <p className="leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Quick links */}
        <section
          className="mt-10 pt-8 border-t border-[#EAEAEA]"
          data-testid="stripe-tax-quick-links"
        >
          <h2 className="font-spectral text-xl text-ink mb-4">Quick links</h2>
          <div className="grid sm:grid-cols-2 gap-3 text-[13.5px]">
            <a
              href="https://docs.stripe.com/tax/connect"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between border border-[#E5E5E5] rounded-lg px-4 py-3 hover:border-gold hover:bg-cream/30 transition-colors"
            >
              <span>Stripe Tax on Connect docs</span>
              <ArrowSquareOut size={14} className="text-gold-dark" />
            </a>
            <a
              href="https://onlineservices.cdtfa.ca.gov/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between border border-[#E5E5E5] rounded-lg px-4 py-3 hover:border-gold hover:bg-cream/30 transition-colors"
            >
              <span>CA CDTFA — Seller&apos;s Permit application</span>
              <ArrowSquareOut size={14} className="text-gold-dark" />
            </a>
            <a
              href="https://docs.stripe.com/tax/registrations"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between border border-[#E5E5E5] rounded-lg px-4 py-3 hover:border-gold hover:bg-cream/30 transition-colors"
            >
              <span>Stripe Tax registration setup</span>
              <ArrowSquareOut size={14} className="text-gold-dark" />
            </a>
            <a
              href="https://www.cdtfa.ca.gov/industry/marketplace-facilitator-act.htm"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between border border-[#E5E5E5] rounded-lg px-4 py-3 hover:border-gold hover:bg-cream/30 transition-colors"
            >
              <span>CA AB 147 marketplace-facilitator law</span>
              <ArrowSquareOut size={14} className="text-gold-dark" />
            </a>
          </div>
        </section>
      </div>
    </AppShell>
  );
}
